import React, { useEffect } from 'react';
import _ from 'lodash';
import styles from './search.page.module.scss';
import {
  Lang,
  SEARCH_TYPES_KEYS,
  SEARCH_PAGE_TYPES,
  SEARCH_TYPE,
  CATEGORY_TYPES,
  ROUTES,
  INTERNAL_ROUTING,
  EVENTS,
} from 'shared/constants';
import { KnowledgeWrapperComponent } from '../knowledge/components/knowledge-row';
import { NotFound } from '../../../components/common';
import { Loader } from '../../../components/core/loader/Loader.component';
import { KnowledgeDeletePopup } from 'src/components/knowledge-content/knowledge-content-editor';
import { SearchType } from '../../../components/searchType';
import { LabValue } from '../lab/components/lab-value/lab-value';
import { LabValueDelete } from '../lab/components/lab-value/lab-value-delete';
import { LabValueEditor } from '../lab/components/lab-value/lab-value-editor';
import { KnowledgeMovePopup } from '../knowledge/components/knowledge-row/knowledgeMove';
import { AdmissionCategoryDelete } from '../admission/components/admission-category/admission-category-delete';
import { AdmissionCategoryEditor } from '../admission/components/admission-category/admission-category-editor';
import { AdmissionCategoryItem } from '../admission/components/admission-category/admission-category-item';
import { useTagFilters } from '../../../components/search';
import { filterByTags, buildSearchSig } from 'shared/utils/search.utils';
import { getExtendedCount } from 'shared/utils/search-v2-mapper';
import { useLang } from 'shared/hooks/lang.hooks';
import { PandaSearchAnswer } from 'src/components/ai';
import analyticsService from '../../../helpers/analytics.service';
import { ContactItemComponent } from '../../../components/contact-item/contact-item.component';
import { useSelector } from 'react-redux';
import {
  isImprovedSearchEnabled,
  isSearchV2Enabled,
} from 'shared/store/selectors/features.selector';
import { useSearchResultsPreparation } from 'shared/hooks/search.hooks';
import { formatNumber, generateUpToDateLink } from 'shared/utils';
import { notification } from 'antd';
import { GiveFeedbackReport } from 'src/components/issue-report/give-feedback-report';
import { SearchGroups } from './search-renderers/search-groups.component';
import { SearchV2Rendering } from './search-renderers/search-v2-rendering.component';
import classNames from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { CategorySearchItem } from './components/category-search-item.component';
import { usePageTitle } from 'src/components/app/use-page-title';
import searchContextIdManager from 'shared/helpers/src/search-context-id-manager';
import { ScreenTitle } from 'src/components/design-system';
import { serializeFiltersToUrl, deserializeFiltersFromUrl } from 'src/utils/search-url-params';
import { updateQueryParamWithExclusivity } from 'src/helpers/routing.helper';
import { useSearchImpression } from './use-search-impression';

function DocumentResult({ item, index, options, contextId, onResultClicked, ...props }) {
  const { ref, impressionId } = useSearchImpression({
    item,
    resultType: SEARCH_TYPES_KEYS.documents,
    contextId,
    options,
  });
  return (
    <div ref={ref}>
      <KnowledgeWrapperComponent
        item={item}
        draggable={false}
        adminMode={props.adminMode}
        onDelete={props.onDelete}
        onUpdate={props.onUpdate}
        onMove={props.onMove}
        onClickEvent={event =>
          onResultClicked(event, index, SEARCH_TYPES_KEYS.documents, options, impressionId)
        }
        light
        showLabels
        showSpecialty
        hideFavorites={item.workspaceId !== props.activeWorkspaceId}
      />
    </div>
  );
}

function CompendiumResult({ item, options, contextId, ...props }) {
  const { ref } = useSearchImpression({
    item,
    resultType: SEARCH_TYPES_KEYS.compendium,
    contextId,
    options,
  });
  return (
    <div ref={ref}>
      <LabValue
        id={item.id}
        item={item}
        draggable={false}
        adminMode={props.adminMode}
        deleteValue={props.deleteValue}
        editValue={props.editValue}
        light
        showSpecialty
      />
    </div>
  );
}

function AdmissionResult({ item, index, options, contextId, onResultClicked, ...props }) {
  const { ref, impressionId } = useSearchImpression({
    item,
    resultType: SEARCH_TYPES_KEYS.admission,
    contextId,
    options,
  });
  return (
    <div ref={ref}>
      <AdmissionCategoryItem
        item={item}
        deleteCategory={props.deleteCategory}
        editCategory={props.editCategory}
        onClickEvent={event =>
          onResultClicked(event, index, SEARCH_TYPES_KEYS.admission, options, impressionId)
        }
        light
      />
    </div>
  );
}

function ContactResult({ item, index, options, contextId, onResultClicked, ...props }) {
  const { ref, impressionId } = useSearchImpression({
    item,
    resultType: SEARCH_TYPES_KEYS.contacts,
    contextId,
    options,
  });
  return (
    <div ref={ref}>
      <ContactItemComponent
        contact={item}
        onClickEvent={event =>
          onResultClicked(event, index, SEARCH_TYPES_KEYS.contacts, options, impressionId)
        }
        hideFavorites={item.workspaceId !== props.activeWorkspaceId}
      />
    </div>
  );
}

function CategoryResult({ item, index, options, contextId, onResultClicked, ...props }) {
  const { ref, impressionId } = useSearchImpression({
    item,
    resultType: SEARCH_TYPES_KEYS.category,
    contextId,
    options,
  });
  return (
    <div ref={ref}>
      <CategorySearchItem
        item={item}
        adminMode={props.adminMode}
        onClickEvent={event =>
          onResultClicked(event, index, SEARCH_TYPES_KEYS.category, options, impressionId)
        }
        knowledgeSelectItem={props.knowledgeSelectItem}
        labSelectValues={props.labSelectValues}
        light
        showLabels
        showSpecialty
      />
    </div>
  );
}

// Module-level variable to persist last tracked URL across component mount/unmount
let lastTrackedSearchUrl = null;
// Reference to the component's lastContextStateRef - set by component on mount
let lastContextStateRefExternal = null;

export const resetSearchTracking = () => {
  lastTrackedSearchUrl = null;
  // Also reset the context state ref to ensure new contextId is generated
  if (lastContextStateRefExternal) {
    lastContextStateRefExternal.current = {
      term: '',
      filters: '',
      searchType: '',
      workspaceId: '',
    };
  }
};

const SearchPageComponent = React.memo(
  ({
    lang = Lang.SEARCH_TYPES,
    langScreen = Lang.SEARCH_SCREEN,
    results = null,
    searching = false,
    term = '',
    adminMode = false,
    lastUpdated = Date.now(),
    knowledgeItem = null,
    id,
    addSearchFilters = () => {},
    updateSearch = () => {},
    isContactsEnabled,
    searchSessionId,
    activeWorkspaceId,
    knowledgeSelectItem = () => {},
    labSelectValues = () => {},
  }) => {
    const navigate = useNavigate();
    const [queryParams, setQueryParams] = useSearchParams();

    // Get term from URL
    const termFromUrl = queryParams.get(INTERNAL_ROUTING.QUERY_PARAMS.SEARCH_TERM) || '';
    const location = useLocation();
    // Minted per submit by the header (same term included). The URL rewrites below
    // carry location.state along, or React Router drops it and the submit id with it.
    const submitId = location.state?.submitId ?? null;

    // Initialize filters from URL on mount only
    const initialFiltersFromUrl = React.useMemo(() => {
      try {
        return deserializeFiltersFromUrl(queryParams);
      } catch (error) {
        console.error('Error deserializing filters from URL:', error);
        return {
          SPECIALTY: new Set(),
          SUBSPECIALTY: new Set(),
          CONTENT_TYPE: new Set(),
          TARGET_AUDIENCE: new Set(),
          LABELS: new Set(),
        };
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only on mount

    const { FilterComponent, filters, filtersExist } = useTagFilters({
      results,
      term,
      initialFilters: initialFiltersFromUrl,
    });

    const [deleteKnowledgeItem, setDeleteKnowledgeItem] = React.useState(null);
    const [deleteLabValue, setDeleteLabValue] = React.useState(null);
    const [labValueEntity, setLabValueEntity] = React.useState(null);
    const [deleteChecklist, setDeleteChecklist] = React.useState(null);
    const [checklistEntity, setChecklistEntity] = React.useState(null);
    // Initialize searchType from URL
    const searchTypeFromUrl = queryParams.get(INTERNAL_ROUTING.QUERY_PARAMS.SEARCH_TYPE) || null;
    const [searchType, setSearchType] = React.useState(searchTypeFromUrl);
    const [knowledgeEntityMove, setKnowledgeEntityMove] = React.useState(null);
    const userId = useSelector(({ auth }) => auth.user.id);
    const [showIssueReportPopup, setShowIssueReportPopup] = React.useState(false);
    const [showExtendedResults, setShowExtendedResults] = React.useState(false);
    // Panda answered: soften the "no results" copy below.
    const [hasPandaAnswer, setHasPandaAnswer] = React.useState(false);
    const pandaLang = useLang('PANDA_IN_SEARCH');
    const [notificationApi, contextHolder] = notification.useNotification();

    // Redirect to homepage if term is empty or missing
    React.useEffect(() => {
      const currentTerm = queryParams.get(INTERNAL_ROUTING.QUERY_PARAMS.SEARCH_TERM);
      if (currentTerm === null || currentTerm.trim() === '') {
        // Navigate to homepage with state to indicate search should be focused
        navigate(ROUTES.HOME, { state: { focusSearch: true }, replace: true });
      }
      // Only run once on mount
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Signature of the last search query fired, shared by the two query effects
    // below to dedupe the duplicate request a term change would otherwise cause.
    const lastQuerySigRef = React.useRef(null);

    // Sync Redux with URL whenever the term changes. Read the filters/tab from
    // the URL (the synchronous source of truth) rather than component state,
    // which lags a render behind on a term change: empty for a brand-new term,
    // the URL's filters for a deep link. Otherwise the query would fire with the
    // previous search's filters.
    React.useEffect(() => {
      if (termFromUrl && termFromUrl !== term) {
        const urlSearchType = queryParams.get(INTERNAL_ROUTING.QUERY_PARAMS.SEARCH_TYPE) || null;
        const filtersForQuery = {
          ...deserializeFiltersFromUrl(queryParams),
          searchType: urlSearchType,
        };
        lastQuerySigRef.current = buildSearchSig(termFromUrl, filtersForQuery);
        updateSearch(termFromUrl, filtersForQuery);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [termFromUrl]); // Only depend on termFromUrl to trigger when URL changes

    // Update URL when filters or searchType change in UI
    React.useEffect(() => {
      // Only update if we have valid term and filters, and term matches URL
      if (term && termFromUrl && term === termFromUrl && filters) {
        const newParams = new URLSearchParams(serializeFiltersToUrl(term, filters));

        // Preserve non-filter params like 'category', 'values', 'checklist'
        const preserveParams = [
          INTERNAL_ROUTING.QUERY_PARAMS.KNOWLEDGE,
          INTERNAL_ROUTING.QUERY_PARAMS.COMPENDIUM,
          INTERNAL_ROUTING.QUERY_PARAMS.CHECKLIST,
        ];
        preserveParams.forEach(param => {
          const value = queryParams.get(param);
          if (value) {
            newParams.set(param, value);
          }
        });

        // Add searchType to URL - use state value or preserve from URL
        const searchTypeToUse =
          searchType || queryParams.get(INTERNAL_ROUTING.QUERY_PARAMS.SEARCH_TYPE);
        if (searchTypeToUse) {
          newParams.set(INTERNAL_ROUTING.QUERY_PARAMS.SEARCH_TYPE, searchTypeToUse);
        }

        const currentParams = queryParams.toString();
        const newParamsString = newParams.toString();

        // Only update if actually different
        if (newParamsString !== currentParams) {
          setQueryParams(newParams, { replace: true, state: location.state });
        }
      }
    }, [filters, searchType, term, termFromUrl, queryParams, setQueryParams, location.state]);

    // Track the last state that generated a contextId to prevent duplicates
    const lastContextStateRef = React.useRef({
      term: '',
      filters: '',
      searchType: '',
      workspaceId: '',
    });
    // Expose ref externally so resetSearchTracking can reset it
    lastContextStateRefExternal = lastContextStateRef;

    // Update context and capture contextId in a single useMemo to prevent multiple renders
    // Using URL state (termFromUrl, queryParams) to ensure contextId syncs with analytics
    const contextId = React.useMemo(() => {
      if (!termFromUrl) return searchContextIdManager.getContextId();
      // Parse filters from URL
      const filtersFromUrl = deserializeFiltersFromUrl(queryParams);

      // Serialize filters for comparison
      const filtersKey = Object.keys(filtersFromUrl)
        .sort()
        .map(k => `${k}:${[...filtersFromUrl[k]].sort().join(',')}`)
        .join('|');

      const currentStateKey = `${termFromUrl}|${filtersKey}|${
        searchType || null
      }|${activeWorkspaceId}`;
      const lastStateKey = `${lastContextStateRef.current.term}|${lastContextStateRef.current.filters}|${lastContextStateRef.current.searchType}|${lastContextStateRef.current.workspaceId}`;

      // Only update context if URL state or workspace actually changed
      if (currentStateKey !== lastStateKey) {
        searchContextIdManager.updateContext(termFromUrl, filtersFromUrl, searchType);
        lastContextStateRef.current = {
          term: termFromUrl,
          filters: filtersKey,
          searchType: searchType || null,
          workspaceId: activeWorkspaceId,
        };
        const id = searchContextIdManager.getContextId();
        return id;
      }

      // Return existing contextId without regenerating
      const id = searchContextIdManager.getContextId();
      return id;
    }, [termFromUrl, queryParams, searchType, activeWorkspaceId]);

    const { updateSubtitle } = usePageTitle();

    useEffect(() => {
      updateSubtitle(term);
    }, [term, updateSubtitle]);

    const trackSearchResultsPageView = analyticsData => {
      analyticsService.track(EVENTS.SEARCH.RESULTS_PAGE_VIEW, analyticsData);
    };

    const debouncedTrackSearchResultsPageView = React.useMemo(
      () =>
        _.debounce((analyticsData, trackingKey) => {
          trackSearchResultsPageView(analyticsData);
          lastTrackedSearchUrl = trackingKey;
        }, 500),
      [],
    );

    const openIssueReportPopup = () => {
      setShowIssueReportPopup(true);
    };

    const closeIssueReportPopup = () => {
      setShowIssueReportPopup(false);
    };

    // Local handler to select binder using URL params instead of Redux action
    const handleKnowledgeSelectItem = React.useCallback(
      id => {
        setQueryParams(
          prev => {
            const newParams = new URLSearchParams(prev);
            updateQueryParamWithExclusivity(newParams, INTERNAL_ROUTING.QUERY_PARAMS.KNOWLEDGE, id);
            return newParams;
          },
          { state: location.state },
        );
        // Still call the Redux action for state management
        knowledgeSelectItem(id);
      },
      [setQueryParams, knowledgeSelectItem, location.state],
    );

    // Local handler to select lab value using URL params
    const handleLabSelectValues = React.useCallback(
      id => {
        setQueryParams(
          prev => {
            const newParams = new URLSearchParams(prev);
            updateQueryParamWithExclusivity(
              newParams,
              INTERNAL_ROUTING.QUERY_PARAMS.COMPENDIUM,
              id,
            );
            return newParams;
          },
          { state: location.state },
        );
        // Still call the Redux action for state management
        labSelectValues(id);
      },
      [setQueryParams, labSelectValues, location.state],
    );

    const filtersByTags = filterByTags({ filters });

    const isImprovedSearch = useSelector(isImprovedSearchEnabled);
    const isSearchV2 = useSelector(isSearchV2Enabled);

    const searchTypes = SEARCH_PAGE_TYPES(lang).filter(
      ({ type }) =>
        (isContactsEnabled || type !== SEARCH_TYPES_KEYS.contacts) &&
        // v2 has no document/media split yet (media maps to documents) — hide the tab.
        (!isSearchV2 || type !== SEARCH_TYPES_KEYS.media),
    );

    const { prepareResults } = useSearchResultsPreparation(
      {
        filtersByTags,
      },
      searchType,
      adminMode,
      isContactsEnabled,
    );

    // Sync searchType from URL when URL changes (e.g., back button)
    React.useEffect(() => {
      setSearchType(searchTypeFromUrl);
      setShowExtendedResults(false);
    }, [searchTypeFromUrl]);

    // Delete knowledge items
    const deleteKnowledgeAction = React.useCallback(({ id, title }) => {
      // TODO: analytics
      setDeleteKnowledgeItem({ id, title });
    }, []);

    const onFinishDelete = React.useCallback(() => {
      setDeleteKnowledgeItem(null);
    }, []);

    const editOrCreateKnowledge = React.useCallback(
      ({ item = {} }) => {
        const targetPath = item.id
          ? ROUTES.KNOWLEDGE_ITEMS_EDIT.replace(':id', item.id)
          : ROUTES.KNOWLEDGE_ITEMS_NEW;
        navigate(targetPath);
      },
      [navigate],
    );

    // Delete value items
    const deleteValueAction = React.useCallback(({ id, title }) => {
      // TODO: analytics
      setDeleteLabValue({ id, title });
    }, []);

    const onFinishDeleteLab = React.useCallback(() => {
      setDeleteLabValue(null);
    }, []);

    // Editor
    const onFinishEditorLab = React.useCallback(() => {
      setLabValueEntity(null);
    }, []);

    const editOrCreateLabValue = React.useCallback(({ item = {} }) => {
      setLabValueEntity(item);
    }, []);
    // Move knowledge items
    const moveKnowledgeAction = React.useCallback(({ id, title, ownerIds }) => {
      // TODO: analytics
      setKnowledgeEntityMove({ id, title, ownerIds });
    }, []);

    const onFinishMove = React.useCallback(() => {
      setKnowledgeEntityMove(null);
    }, []);

    // checklist delete
    const deleteChecklistAction = React.useCallback(({ id, title }) => {
      // TODO: analytics
      setDeleteChecklist({ id, title });
    }, []);

    const onFinishDeleteChecklist = React.useCallback(() => {
      setDeleteChecklist(null);
    }, []);

    // checklist editor
    const onFinishEditChecklist = React.useCallback(() => {
      setChecklistEntity(null);
    }, []);

    const editOrCreateChecklist = React.useCallback(
      ({ id, title, ownerIds, path, tags, type, keywords }) => {
        setChecklistEntity({ id, title, ownerIds, path, tags, type, keywords });
      },
      [],
    );
    const {
      resultsCount: legacyResultsCount,
      knowledgeItems,
      labItems,
      admissionItems,
      contactItems,
      knowledgeCategories,
      labCategories,
      admissionCategories,
    } = isSearchV2 ? {} : prepareResults(results, true);

    const resultsCount = isSearchV2 ? results?.count || 0 : legacyResultsCount || 0;

    // Prepare extended/fuzzy results for analytics
    const extendedSearchResults = React.useMemo(
      () =>
        isImprovedSearch && results?.extendedSearch
          ? prepareResults(results.extendedSearch, true)
          : {},
      [isImprovedSearch, results?.extendedSearch, prepareResults],
    );

    /**
     * Track click on result
     * @param {Object} item - result item details
     * @param {number} index - result index
     * @param {string} resultType - result type
     * @param {Object} options - Options for analytics
     */
    const onResultClicked = React.useCallback(
      (item, index, resultType, options, impressionId) => {
        const eventDetails = {
          term,
          searchSessionId,
          index: index + 1,
          resultsCount,
          score: item?.score || 0,
          resultType,
          ...item,
          ...options,
          impression_id: impressionId,
        };

        analyticsService.track(EVENTS.SEARCH.RESULT_CLICKED, { ...eventDetails, contextId });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [searchSessionId, resultsCount],
    );

    // Prepare render by type - each entry creates a component so useAnalyticsImpression can track impressions
    const sharedResultProps = {
      contextId,
      onResultClicked,
      adminMode,
      activeWorkspaceId,
    };
    const renderByType = React.useMemo(
      () => ({
        [SEARCH_TYPES_KEYS.documents]: ({ item }, index, options = {}) => (
          <DocumentResult
            key={`${item.id}-${contextId}`}
            item={item}
            index={index}
            options={options}
            {...sharedResultProps}
            onDelete={deleteKnowledgeAction}
            onUpdate={editOrCreateKnowledge}
            onMove={moveKnowledgeAction}
          />
        ),
        [SEARCH_TYPES_KEYS.compendium]: ({ item }, index, options = {}) => (
          <CompendiumResult
            key={`${item.id}-${contextId}`}
            item={item}
            options={options}
            {...sharedResultProps}
            deleteValue={deleteValueAction}
            editValue={editOrCreateLabValue}
          />
        ),
        [SEARCH_TYPES_KEYS.admission]: ({ item }, index, options = {}) => (
          <AdmissionResult
            key={`${item.id}-${contextId}`}
            item={item}
            index={index}
            options={options}
            {...sharedResultProps}
            deleteCategory={deleteChecklistAction}
            editCategory={editOrCreateChecklist}
          />
        ),
        [SEARCH_TYPES_KEYS.contacts]: ({ item }, index, options = {}) => (
          <ContactResult
            key={`${item.id}-${contextId}`}
            item={item}
            index={index}
            options={options}
            {...sharedResultProps}
          />
        ),
        [SEARCH_TYPES_KEYS.category]: ({ item }, index, options = {}) => (
          <CategoryResult
            key={`${item.id}-${contextId}`}
            item={item}
            index={index}
            options={options}
            {...sharedResultProps}
            knowledgeSelectItem={handleKnowledgeSelectItem}
            labSelectValues={handleLabSelectValues}
          />
        ),
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        adminMode,
        searchSessionId,
        onResultClicked,
        activeWorkspaceId,
        contextId,
        handleKnowledgeSelectItem,
        handleLabSelectValues,
      ],
    );

    // Re-key renderByType for v2: dispatch is on the API's entity `type`.
    // Admissions render as a category card when item.type === CATEGORY,
    // matching the visual split the legacy per-bucket structure produced.
    const renderByTypeV2 = React.useMemo(
      () => ({
        knowledgeItems: renderByType[SEARCH_TYPES_KEYS.documents],
        knowledgeCategories: renderByType[SEARCH_TYPES_KEYS.category],
        labValues: renderByType[SEARCH_TYPES_KEYS.compendium],
        labCategories: renderByType[SEARCH_TYPES_KEYS.category],
        admissions: ({ item }, index, options) =>
          item.type === CATEGORY_TYPES.CATEGORY
            ? renderByType[SEARCH_TYPES_KEYS.category]({ item }, index, options)
            : renderByType[SEARCH_TYPES_KEYS.admission]({ item }, index, options),
        contact: renderByType[SEARCH_TYPES_KEYS.contacts],
        users: renderByType[SEARCH_TYPES_KEYS.contacts],
      }),
      [renderByType],
    );

    const renderResultsRegular = React.useCallback(() => {
      const options = { searchType: SEARCH_TYPE.REGULAR };

      const renderV2 = () => {
        // `count` is the API's own total; fall back to the hydrated extended tier so a
        // fuzzy-only query never shows the empty state while related results exist.
        const extendedCount = getExtendedCount(results);
        const isEmptyState = resultsCount === 0 && extendedCount === 0;
        return (
          <>
            <SearchType searchType={searchType} types={searchTypes} onChange={setSearchType} />
            {isEmptyState && !hasPandaAnswer && (
              <NotFound className={styles.notFound} text={langScreen.NO_ITEMS} />
            )}
            {!isEmptyState && (
              <SearchV2Rendering
                results={results}
                renderByType={renderByTypeV2}
                options={options}
              />
            )}
          </>
        );
      };

      const renderV1 = () => {
        const extendedSearch = isImprovedSearch ? prepareResults(results.extendedSearch, true) : {};
        const isEmptyState =
          resultsCount === 0 && (!isImprovedSearch || extendedSearch?.resultsCount === 0);
        const allResults = {
          contactItems,
          knowledgeItems,
          labItems,
          admissionItems,
          knowledgeCategories,
          labCategories,
          admissionCategories,
        };
        return (
          <>
            <SearchType searchType={searchType} types={searchTypes} onChange={setSearchType} />
            {isEmptyState && !hasPandaAnswer && (
              <NotFound className={styles.notFound} text={langScreen.NO_ITEMS} />
            )}
            {!isEmptyState && (
              <SearchGroups
                results={allResults}
                renderByType={renderByType}
                options={options}
                extendedSearch={extendedSearch}
              />
            )}
          </>
        );
      };

      return (
        <React.Fragment key={lastUpdated}>
          {isSearchV2 ? renderV2() : renderV1()}
          {renderUpToDateLink()}
        </React.Fragment>
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      adminMode,
      results,
      searchType,
      showExtendedResults,
      prepareResults,
      isSearchV2,
      hasPandaAnswer,
    ]);

    // Shared by renderResults, renderResultsTitle, and the filter column: the
    // title can never render over a loader, and the filters must not outlive
    // their results (they derive from the previous term's data while pending).
    const isSearchPending =
      (termFromUrl && termFromUrl !== term) || term === '' || _.isNull(results) || searching;

    const renderResults = React.useCallback(() => {
      if (isSearchPending) {
        // Empty term with no query in flight is the idle empty state, not a loader.
        if (term === '' && !searching && !(termFromUrl && termFromUrl !== term)) {
          return (
            <NotFound
              style={styles.notFound}
              text={lang.NO_ITEMS}
              textStyle={styles.notFoundText}
            />
          );
        }
        return (
          <div className={styles.loader}>
            <Loader />
          </div>
        );
      }

      return renderResultsRegular();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      isImprovedSearch,
      adminMode,
      lastUpdated,
      results,
      searchType,
      searching,
      term,
      termFromUrl,
      showExtendedResults,
      renderResultsRegular,
    ]);

    const renderResultsTitle = () => {
      if (isSearchPending) return null;

      if (resultsCount > 0) {
        return (
          <ScreenTitle
            title={langScreen.SEARCH_RESULTS_TITLE.replace("'{term}'", term)}
            subtitle={langScreen.SEARCH_RESULTS_COUNT.replace(
              '{number}',
              formatNumber(resultsCount),
            )}
          />
        );
      }

      const extendedCount = isSearchV2
        ? getExtendedCount(results)
        : extendedSearchResults?.resultsCount;
      // Panda answered but no documents matched. Say it neutrally — the user
      // did get something, so the page shouldn't read as a failure.
      const noResultsTitle = hasPandaAnswer
        ? pandaLang.NO_MATCHING_ITEMS
        : langScreen.NO_RESULTS_FOUND.replace('{term}', term);
      return (
        <ScreenTitle
          title={
            extendedCount > 0
              ? langScreen.ONLY_FUZZY_RESULTS.replace('{term}', term)
              : noResultsTitle
          }
        />
      );
    };

    const renderUpToDateLink = React.useCallback(() => {
      const [first, upToDateLink, third, reportIssueLink] =
        langScreen.UP_TO_DATE_LINK.split(/{(.*?)}/);

      return (
        <div className={styles.upToDateLink}>
          <p>
            {first}
            <a target="_blank" rel="noopener noreferrer" href={generateUpToDateLink(term, userId)}>
              {upToDateLink}
            </a>
            {third}
            <button
              type="button"
              onClick={openIssueReportPopup}
              className={styles.reportIssueButton}>
              {reportIssueLink}
            </button>
          </p>
        </div>
      );
    }, [term, userId, langScreen.UP_TO_DATE_LINK]);

    useEffect(() => {
      if (!term) return;
      const filtersForQuery = { ...filters, searchType };
      const sig = buildSearchSig(term, filtersForQuery);
      // A term change auto-resets the tag filters; that reset re-enters this
      // effect with the same query the term-sync effect just fired. Skip the
      // duplicate; only fire for filter/tab changes that produce a new query.
      if (sig === lastQuerySigRef.current) return;
      lastQuerySigRef.current = sig;
      addSearchFilters(filtersForQuery);
      // term is read for the signature but intentionally omitted from deps — the
      // term-sync effect owns term-change queries; this fires on filter/tab change.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addSearchFilters, filters, searchType]);

    useEffect(() => {
      if (!termFromUrl || !searchSessionId) {
        return;
      }

      // Don't track if we don't have results yet or still searching
      if (!results || searching) {
        return;
      }

      // Don't track if Redux term doesn't match URL term (results are stale)
      if (term !== termFromUrl) {
        return;
      }

      // Exclude viewer params (category, values, checklist) from tracking key
      // so opening a viewer doesn't trigger a new results page view event
      const trackingParams = new URLSearchParams(queryParams);
      trackingParams.delete(INTERNAL_ROUTING.QUERY_PARAMS.KNOWLEDGE);
      trackingParams.delete(INTERNAL_ROUTING.QUERY_PARAMS.COMPENDIUM);
      trackingParams.delete(INTERNAL_ROUTING.QUERY_PARAMS.CHECKLIST);
      const trackingKey = `${trackingParams.toString()}|searchType:${searchType || null}`;

      // If tracking key changed (new search/filter/type), cancel any pending calls
      if (lastTrackedSearchUrl !== trackingKey) {
        debouncedTrackSearchResultsPageView.cancel();
      }

      // Only track if the URL actually changed (excluding viewer params) or searchType changed
      if (lastTrackedSearchUrl === trackingKey) {
        return;
      }

      // Guard: Don't track if we don't have results yet (wait for results to load)
      if (!results) {
        return;
      }

      // Parse filters from URL
      const filtersFromUrl = deserializeFiltersFromUrl(queryParams);

      // Calculate numberOfResults based on top results and extended/fuzzy results
      // TODO: Switch to simple result count once result calculation is fixed - also in mobile
      // Current issue: When there are no top results but extended/fuzzy results exist,
      // analytics reports 0 results while users see fuzzy matches on screen.
      // This creates a mismatch between reported and actual user experience.
      let numberOfResults;
      const extendedResultsCount = extendedSearchResults?.resultsCount || 0;

      if (resultsCount === 0 && extendedResultsCount === 0) {
        // No results at all - truly empty
        numberOfResults = 0;
      } else if (resultsCount === 0 && extendedResultsCount > 0) {
        // Only fuzzy/extended results, no top results
        numberOfResults = -1;
      } else {
        // Has top results
        numberOfResults = resultsCount;
      }

      // Build analytics data based on URL state
      const analyticsData = {
        contextId,
        searchTerm: termFromUrl,
        specialty_filter: [...filtersFromUrl.SPECIALTY],
        sub_specialty_filter: [...filtersFromUrl.SUBSPECIALTY],
        content_type_filter: [...filtersFromUrl.CONTENT_TYPE],
        target_audience_filter: [...filtersFromUrl.TARGET_AUDIENCE],
        labels_filter: [...filtersFromUrl.LABELS],
        numberOfResults,
        searchSessionId,
        isFiltered: Object.values(filtersFromUrl).some(v => v.size > 0) || !!searchType,
        searchType: searchType || null,
        activeWorkspace: activeWorkspaceId,
      };

      debouncedTrackSearchResultsPageView(analyticsData, trackingKey);
    }, [
      term,
      termFromUrl,
      queryParams,
      searchSessionId,
      results,
      searching,
      resultsCount,
      contextId,
      extendedSearchResults,
      searchType,
      activeWorkspaceId,
      debouncedTrackSearchResultsPageView,
    ]);

    return (
      <div className={classNames(['new-design-system', styles.root])}>
        <GiveFeedbackReport
          open={showIssueReportPopup}
          close={closeIssueReportPopup}
          notificationApi={notificationApi}
        />
        {contextHolder}

        {/* Keeps the title's footprint while loading so the content below doesn't jump. */}
        <div className={styles.titleSlot}>{renderResultsTitle()}</div>
        <div className={styles.columns}>
          <div className={styles.searchResults}>
            {/* Outside renderResults(): inside, the loader→results swap remounted
                the card and re-POSTed the run. */}
            <div className={classNames((!filtersExist || isSearchPending) && styles.answerCapped)}>
              <PandaSearchAnswer
                term={termFromUrl}
                submitId={submitId}
                contextId={contextId}
                sessionId={searchSessionId}
                hidden={isSearchPending}
                onHasAnswerChange={setHasPandaAnswer}
              />
            </div>
            {renderResults()}
          </div>
          {filtersExist && !isSearchPending && (
            <div className={styles.filterBox}>{FilterComponent}</div>
          )}
        </div>

        <KnowledgeDeletePopup
          open={!!deleteKnowledgeItem}
          entity={deleteKnowledgeItem}
          onFinish={onFinishDelete}
        />

        <LabValueDelete
          open={!!deleteLabValue}
          entity={deleteLabValue}
          onFinish={onFinishDeleteLab}
        />

        <LabValueEditor
          open={!!labValueEntity}
          value={labValueEntity}
          onFinish={onFinishEditorLab}
        />

        <KnowledgeMovePopup
          categoryId={id}
          item={knowledgeEntityMove}
          onFinish={onFinishMove}
          isOpen={!!knowledgeEntityMove}
          enableCreateLink
        />

        {deleteChecklist && (
          <AdmissionCategoryDelete entity={deleteChecklist} onFinish={onFinishDeleteChecklist} />
        )}
        <AdmissionCategoryEditor
          open={!!checklistEntity}
          entity={checklistEntity}
          onFinish={onFinishEditChecklist}
        />
      </div>
    );
  },
);

export { SearchPageComponent };
